/**
 * intelSourceV2.js
 * Google Sheets CSV fetcher for Intelligence Reports.
 *
 * Parsing strategy: name-based (not position-based).
 * The parser reads the sheet's actual header row and maps columns by name.
 * Five sheet headers use human-readable labels that differ from the internal
 * code field names — HEADER_ALIASES resolves these automatically, so the
 * sheet can keep its plain-English column labels without any code changes.
 *
 * Falls back to legacy intel.js data if the sheet is unavailable or missing
 * the required core fields (id, ref_id, title, date, category).
 */
import { useState, useEffect } from 'react';
import { softenDeep } from '@/lib/brandCopy';
import { INTEL_REPORTS } from '@/constants/intel';

// Public published-to-web sheet. Hardcoded as a runtime fallback so
// CI/CD builds without a `.env` file (it's gitignored) still ship
// working sheet fetching. Override locally via REACT_APP_INTEL_SHEET_ID.
const DEFAULT_INTEL_SHEET_ID = '1HaZa-xdNleBwG6Hlxfp03jBmWUwQj5kv1PQCqFdS77g';
const SHEET_ID = process.env.REACT_APP_INTEL_SHEET_ID || DEFAULT_INTEL_SHEET_ID;
const SHEET_CSV_URL = SHEET_ID
  ? `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv`
  : null;

// Cache key — bump suffix to invalidate stale cached data.
// v7: added process_before, process_after, process_step_types, tech_stack_detail, 
//     architecture_pattern, client_testimonial, other_benefits
// v8: tolerant parsing for process steps (-> and | separators), semantic step types, name-only tech stack
// v9: added friction_points, metrics_before
// v10: RFC-4180 CSV parser (handles newlines inside quoted cells)
// v11: derive `type` = 'case_study' | 'briefing' for hub segmentation
// v12: stricter case_study heuristic (requires friction_points|process_after|testimonial)
// v13: accept human-readable Type / Report Type / Content Type / Stream headers
// v14: cache-bust to force Hero "Latest Intelligence" filter (case_study only) refresh
// v15: Hero filter reworked — Type column authoritative, ref_id prefix fallback
const CACHE_KEY = 'intel_reports_v16'; // v16: brand copy rules applied at load
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

// V2_HEADER is kept as a canonical reference and is used only when the sheet
// has no header row (should not happen in practice).
const V2_HEADER = [
  'id','ref_id','title','eyebrow_tag','headline','category','sector','author_node',
  'date','read_time','pinned','ppt_coverage','tags','tech_stack','desc',
  'operational_friction','strategic_pivot','commercial_roi',
  'metric1','metric2','metric3',
  'executive_abstract','business_friction','intervention','outcome',
  'challenge','solution','business_impact_summary','operational_deep_dive','technical_implementation',
  'isFAQ','faq_q1','faq_a1','faq_q2','faq_a2','faq_q3','faq_a3',
  'pdf_file_id','visual_emphasis',
  'process_before','process_after','process_step_types','tech_stack_detail',
  'architecture_pattern','client_testimonial','other_benefits',
  'friction_points','metrics_before'
];

// Map from Google Sheet column labels → internal code field names.
// Allows the sheet to use human-readable headers while the code stays stable.
// Add entries here whenever a sheet column is renamed without changing the code.
const HEADER_ALIASES = {
  'The Challenge':            'challenge',
  'Our Solution':             'solution',
  'Business Impact':          'business_impact_summary',
  'Operational Deep-Dive':    'operational_deep_dive',
  'Technical Implementation': 'technical_implementation',
  // Manual case-study vs briefing override (v11+)
  'Type':                     'type',
  'Report Type':              'type',
  'Content Type':             'type',
  'Stream':                   'type',
};

// --- CSV Parser (RFC-4180 compliant, handles newlines inside quoted cells) ---
// A previous line-based `csvText.split('\n')` parser broke as soon as any
// sheet cell contained a newline (Google Sheets stores multi-line text with
// literal \n inside the exported CSV). This parser is character-based and
// tolerates quoted fields, escaped quotes ("") and embedded newlines.
// Ported from teamSource.js for consistency.
function parseCSVRows(csvText) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const c = csvText[i];

    if (inQuotes) {
      if (c === '"') {
        // Escaped quote?
        if (csvText[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }

    if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\r') {
      // Normalize CRLF — skip and let \n handle it
    } else if (c === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += c;
    }
  }

  // Flush trailing field / row if the file doesn't end with a newline
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Strip trailing fully-empty rows
  while (rows.length && rows[rows.length - 1].every((v) => !v || !v.trim())) {
    rows.pop();
  }

  // Trim every cell — cells are already unquoted by the state machine above.
  return rows.map((r) => r.map((cell) => (cell || '').trim()));
}

function parseCSV(csvText) {
  const rows = parseCSVRows(csvText);
  if (rows.length < 2) return null;

  const cleanFirst = rows[0];
  const hasHeader = cleanFirst[0] === 'id';

  // ── Name-based path (sheet has header row) ───────────────────────────
  if (hasHeader) {
    // Resolve human-readable column labels → internal field names via alias map.
    // Columns not in HEADER_ALIASES are kept as-is.
    const resolvedHeader = cleanFirst.map((h) => HEADER_ALIASES[h] || h);

    // Soft validation: the five core identity/temporal fields must be present.
    const CORE_FIELDS = ['id', 'ref_id', 'title', 'date', 'category'];
    if (!CORE_FIELDS.every((f) => resolvedHeader.includes(f))) {
      console.warn('[IntelSourceV2] Sheet is missing core fields — falling back to legacy data.');
      return null;
    }

    const reports = [];

    for (let r = 1; r < rows.length; r++) {
      const cols = rows[r];
      if (cols.length < 5) continue; // skip obviously malformed rows

      const obj = {};
      resolvedHeader.forEach((key, idx) => {
        if (key) {
          obj[key] = cols[idx] || '';
        }
      });

      // Rows missing the identity fields are almost certainly padding
      if (!obj.id) continue;

      reports.push(softenDeep(normalizeRow(obj)));
    }

    return reports.length > 0 ? reports : null;
  }

  // ── Position-based fallback (no header row — legacy sheets) ──────────
  // Use V2_HEADER order. Reject if column count doesn't match exactly.
  if (cleanFirst.length !== V2_HEADER.length) return null;

  const reports = [];
  for (const cols of rows) {
    if (cols.length < 15) continue;
    const obj = {};
    V2_HEADER.forEach((key, idx) => {
      obj[key] = cols[idx] || '';
    });
    reports.push(softenDeep(normalizeRow(obj)));
  }
  return reports.length > 0 ? reports : null;
}

/**
 * Split process steps on BOTH pipe | AND arrow -> (with or without whitespace)
 */
function splitProcessSteps(raw) {
  if (!raw) return [];
  const s = String(raw).trim();
  if (!s) return [];
  // Split on -> OR | (with optional surrounding whitespace)
  return s.split(/\s*(?:->|\|)\s*/).map(x => x.trim()).filter(Boolean);
}

/**
 * Normalize a step type string into {kind: 'human'|'auto', label: string}
 * - 'human', 'manual', 'review' → {kind: 'human', label: <original>}
 * - 'auto', 'automated' → {kind: 'auto', label: ''}
 * - Semantic labels (Ingestion, Analysis, Generation, etc.) → {kind: 'auto', label: <original>}
 */
function normalizeStepType(raw) {
  if (!raw) return { kind: 'auto', label: '' };
  const t = String(raw).trim().toLowerCase();
  if (t === 'human' || t === 'manual' || t === 'review') return { kind: 'human', label: raw.trim() };
  if (t === 'auto' || t === 'automated') return { kind: 'auto', label: '' };
  // Semantic label (Ingestion, Analysis, Generation, etc.) — treat as auto with label
  return { kind: 'auto', label: raw.trim() };
}

function normalizeRow(obj) {
  const fileId = extractDriveFileId(obj.pdf_file_id);
  
  // Parse process_before: split on both pipe | and arrow -> separators
  const processBefore = splitProcessSteps(obj.process_before);
  
  // Parse process_after: split on both pipe | and arrow -> separators
  const processAfter = splitProcessSteps(obj.process_after);
  
  // Parse process_step_types: split on pipe, normalize to {kind, label}
  const processStepTypes = obj.process_step_types 
    ? obj.process_step_types.split(/\s*\|\s*/).map(normalizeStepType).filter(Boolean)
    : [];
  
  // Parse tech_stack_detail: pipe-separated, allow items without :: separator
  const techStackDetail = obj.tech_stack_detail 
    ? obj.tech_stack_detail.split(/\s*\|\s*/).map(item => {
        const [name, ...purposeParts] = item.split('::');
        return {
          name: (name || '').trim(),
          purpose: purposeParts.join('::').trim()
        };
      }).filter(t => t.name) // Only keep entries with a name
    : [];
  
  // Parse client_testimonial: pipe-separated "QUOTE::ATTRIBUTION" pairs → array of {quote, attribution}
  const clientTestimonial = obj.client_testimonial 
    ? obj.client_testimonial.split('|').map(pair => {
        const parts = pair.split('::');
        return {
          quote: (parts[0] || '').trim(),
          attribution: (parts[1] || '').trim()
        };
      }).filter(t => t.quote) // Only keep entries with a quote
    : [];
  
  // Parse other_benefits: pipe-separated string → array
  const otherBenefits = obj.other_benefits 
    ? obj.other_benefits.split('|').map(s => s.trim()).filter(Boolean) 
    : [];
  
  // Parse friction_points: pipe-separated triples with :: internal separator
  // Format: "icon::label::value|icon::label::value"
  const frictionPoints = obj.friction_points
    ? obj.friction_points.split(/\s*\|\s*/).map(triple => {
        const parts = triple.split('::').map(x => x.trim());
        return {
          icon: (parts[0] || '').toLowerCase(),
          label: parts[1] || '',
          value: parts[2] || '',
        };
      }).filter(x => x.label || x.value)
    : [];
  
  // Parse metrics_before: pipe-separated strings aligned with metric1/2/3
  const metricsBefore = obj.metrics_before
    ? obj.metrics_before.split(/\s*\|\s*/).map(x => x.trim()).filter(Boolean)
    : [];

  // Derive report type: 'case_study' vs 'briefing'.
  // Editors may override via a `type` column; otherwise apply heuristic:
  //   case_study  = has a quantitative metric AND at least one of:
  //                   friction_points (operational grid), process_after (before/after
  //                   process map), OR a client testimonial. These fields are only
  //                   populated for real engagements — not thought-leadership briefings.
  //   briefing    = everything else (frameworks, playbooks, boardroom briefings).
  const rawType = (obj.type || '').trim().toLowerCase();
  let derivedType;
  if (rawType === 'case_study' || rawType === 'case-study' || rawType === 'case study') {
    derivedType = 'case_study';
  } else if (rawType === 'briefing' || rawType === 'intel' || rawType === 'thought_leadership') {
    derivedType = 'briefing';
  } else {
    const hasMetric = Boolean(obj.metric1 || obj.metric2 || obj.metric3);
    const hasFriction = frictionPoints.length > 0;
    const hasProcess = processAfter.length > 0;
    const hasTestimonial = clientTestimonial.length > 0;
    derivedType = (hasMetric && (hasFriction || hasProcess || hasTestimonial)) ? 'case_study' : 'briefing';
  }
  
  return {
    ...obj,
    pinned: obj.pinned === 'TRUE' || obj.pinned === true,
    isFAQ: obj.isFAQ === 'TRUE' || obj.isFAQ === true,
    visual_emphasis: obj.visual_emphasis === 'TRUE' || obj.visual_emphasis === true,
    ppt_coverage: obj.ppt_coverage ? obj.ppt_coverage.split('|').filter(Boolean) : [],
    tags: obj.tags ? obj.tags.split('|').filter(Boolean) : [],
    tech_stack: obj.tech_stack ? obj.tech_stack.split('|').filter(Boolean) : [],
    // Inline-preview URL — opens the PDF in a new tab without forcing
    // a download (Google's `/file/d/<id>/preview` viewer).
    pdf_preview_url: fileId ? `https://drive.google.com/file/d/${fileId}/preview` : null,
    // Forced-download URL — used by the explicit "Download" CTA.
    pdf_download_url: fileId ? `https://drive.google.com/uc?export=download&id=${fileId}` : null,
    // New v7 fields
    process_before: processBefore,
    process_after: processAfter,
    process_step_types: processStepTypes,
    tech_stack_detail: techStackDetail,
    architecture_pattern: obj.architecture_pattern || '',
    client_testimonial: clientTestimonial,
    other_benefits: otherBenefits,
    friction_points: frictionPoints,
    metrics_before: metricsBefore,
    // v11: derived stream — 'case_study' | 'briefing'
    type: derivedType,
  };
}

/**
 * Build a working Google Drive download URL from whatever the editor
 * pasted into the `pdf_file_id` column. Accepts:
 *   • a bare file ID: `15ZXnfBOL6SnYNSo_xZhXUwWKtNBgQyaz`
 *   • a "Get link" share URL: `https://drive.google.com/file/d/<id>/view?usp=drive_link`
 *   • a legacy open URL: `https://drive.google.com/open?id=<id>`
 *   • a docs URL: `https://docs.google.com/<type>/d/<id>/edit`
 * Returns null when nothing parsable is found.
 */
function buildDrivePdfUrl(raw) {
  const fileId = extractDriveFileId(raw);
  if (!fileId) return null;
  return `https://drive.google.com/uc?export=download&id=${fileId}`;
}

function extractDriveFileId(raw) {
  if (!raw) return '';
  const s = String(raw).trim();
  if (!s) return '';
  // Bare ID (Drive file IDs are typically 25-44 chars of base64url)
  if (/^[a-zA-Z0-9_-]{20,}$/.test(s)) return s;
  // /d/<id>/ pattern (file/d/<id>/view, document/d/<id>/edit, etc.)
  const dMatch = s.match(/\/d\/([a-zA-Z0-9_-]{20,})/);
  if (dMatch) return dMatch[1];
  // ?id=<id> or &id=<id> pattern
  const idMatch = s.match(/[?&]id=([a-zA-Z0-9_-]{20,})/);
  if (idMatch) return idMatch[1];
  // Fallback: longest plausibly-Drive-ID substring
  const longest = s.match(/[a-zA-Z0-9_-]{25,}/);
  return longest ? longest[0] : '';
}

// --- Legacy Mapper: convert intel.js format to v2 format ---
function mapLegacyReport(r) {
  const legacyFaqs = r.faq_sections || [];
  return {
    id: r.id,
    ref_id: r.ref_id,
    title: r.title,
    eyebrow_tag: r.eyebrow_tag || `USE CASE // ${r.sector}`,
    headline: r.headline || r.title,
    category: r.category,
    sector: r.sector,
    author_node: r.author_node,
    date: r.date,
    read_time: r.read_time,
    pinned: r.pinned || false,
    ppt_coverage: r.ppt_coverage || [],
    tags: r.tags || [],
    tech_stack: r.tech_stack ? (typeof r.tech_stack === 'string' ? [r.tech_stack] : r.tech_stack) : [],
    desc: r.desc || '',
    operational_friction: r.business_friction || r.desc || '',
    strategic_pivot: r.intervention || '',
    commercial_roi: r.outcome || '',
    metric1: r.impact_radius?.metric1 || '',
    metric2: r.impact_radius?.metric2 || '',
    metric3: r.impact_radius?.metric3 || '',
    executive_abstract: r.executive_abstract || '',
    business_friction: r.business_friction || '',
    intervention: r.intervention || '',
    outcome: r.outcome || '',
    challenge: '',
    solution: '',
    business_impact_summary: '',
    operational_deep_dive: '',
    technical_implementation: '',
    isFAQ: r.isFAQ || false,
    faq_q1: legacyFaqs[0]?.question || '',
    faq_a1: legacyFaqs[0]?.answer || '',
    faq_q2: legacyFaqs[1]?.question || '',
    faq_a2: legacyFaqs[1]?.answer || '',
    faq_q3: legacyFaqs[2]?.question || '',
    faq_a3: legacyFaqs[2]?.answer || '',
    pdf_file_id: '',
    pdf_preview_url: null,
    pdf_download_url: null,
    visual_emphasis: r.visual_emphasis || false,
    // New v7 fields (empty defaults)
    process_before: [],
    process_after: [],
    process_step_types: [],
    tech_stack_detail: [],
    architecture_pattern: '',
    client_testimonial: [],
    other_benefits: [],
    friction_points: [],
    metrics_before: [],
    // Preserve legacy fields for DossierDetail
    sensitivity: r.sensitivity,
    visual_metrics: r.visual_metrics,
    system_config: r.system_config,
    execution_protocol: r.execution_protocol,
    diagram_type: r.diagram_type,
  };
}

const LEGACY_FALLBACK = INTEL_REPORTS.map(mapLegacyReport);

// --- Cache Helpers ---
function getCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
}

function setCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (_e) { /* quota exceeded — silently skip */ }
}

export function clearIntelCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch (_e) { /* ignore */ }
}

// --- Main Hook ---
export function useIntelReports() {
  const [reports, setReports] = useState(LEGACY_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('legacy');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      // 1. Check cache
      const cached = getCache();
      if (cached) {
        if (!cancelled) {
          setReports(cached);
          setSource('cache');
          setLoading(false);
        }
        return;
      }

      // 2. Try Google Sheet
      if (!SHEET_CSV_URL) {
        if (!cancelled) { setLoading(false); }
        return;
      }

      try {
        const res = await fetch(SHEET_CSV_URL, { signal: AbortSignal.timeout(8000) });
        if (!res.ok) throw new Error('Sheet fetch failed');
        const text = await res.text();
        const parsed = parseCSV(text);

        if (parsed && !cancelled) {
          setCache(parsed);
          setReports(parsed);
          setSource('sheet');
        }
      } catch (err) {
        console.warn('[IntelSourceV2] Sheet fetch failed, using legacy data:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return { reports, loading, source };
}

// --- Derive dynamic categories from loaded reports ---
export function deriveCategories(reports) {
  const base = [{ id: 'all', label: 'ALL' }];
  const seen = new Set();
  const custom = [];
  for (const r of reports) {
    if (r.category && !seen.has(r.category)) {
      seen.add(r.category);
      custom.push({
        id: r.category,
        label: r.category.replace(/-/g, ' ').toUpperCase()
      });
    }
  }
  return [...base, ...custom];
}
